import { put, call, takeEvery } from "redux-saga/effects";
import request from "utils/api";
import _pick from "lodash/pick";
import _isEmpty from "lodash/isEmpty";
import { safe } from "providers/GeneralProvider/saga";
import Helper from "utils/helpers";
import {
  getContestListRequest,
  getContestListSuccess,
  createContestRequest,
  createContestSuccess,
  updateContestRequest,
  updateContestSuccess,
  deleteContestRequest,
  deleteContestSuccess,
  getContestDetailRequest,
  getContestDetailSuccess,
  stopLoading,
  updateVotingLogicRequest,
  updateVotingLogicSuccess,
  getActiveContestsRequest,
  getActiveContestsSuccess,
  getVoteHistoryRequest,
  getVoteHistorySuccess,
} from "./slice";

function* getContestList(action) {
  try {
    const params = _pick(action.payload, [
      "page",
      "limit",
      "keyword",
      "sortField",
      "sortOrder",
      "status",
    ]);

    // Set default values if not provided
    if (!params.page) params.page = 1;
    if (!params.limit) params.limit = 10;
    if (!params.sortField) params.sortField = "createdAt";
    if (!params.sortOrder) params.sortOrder = -1;

    // Handle keyword processing if needed
    if (!_isEmpty(params.keyword)) {
      params.keyword =
        Helper.sortStringWithoutSpecialChars(params.keyword) || undefined;
    }

    const { data } = yield call(request, "api/contest/admin/contests", params, {
      method: "GET",
    });

    yield put(getContestListSuccess(data));
  } catch (error) {
    yield put(stopLoading());
  }
}

function* createContest(action) {
  try {
    const contestData = action.payload;
    const { meta } = action;

    const data = yield call(
      request,
      "api/contest/admin/contests",
      contestData,
      {
        method: "POST",
      }
    );

    yield put(createContestSuccess(data, meta));
  } catch (error) {
    yield put(stopLoading());
    throw error; // Re-throw the error so the component can catch it
  }
}

function* updateContest(action) {
  try {
    const { contestId, contestData } = action.payload;
    const { meta } = action;

    const data = yield call(
      request,
      `api/contest/admin/contests/${contestId}`,
      contestData,
      {
        method: "PUT",
      }
    );

    yield put(updateContestSuccess(data, meta));
  } catch (error) {
    yield put(stopLoading());
    throw error;
  }
}

function* deleteContest(action) {
  try {
    const contestId = action.payload;
    const { meta } = action;

    const data = yield call(
      request,
      `api/contest/admin/contests/${contestId}`,
      {},
      {
        method: "DELETE",
      }
    );

    yield put(deleteContestSuccess(data, meta));
  } catch (error) {
    yield put(stopLoading());
    throw error;
  }
}

function* getContestDetail(action) {
  try {
    const contestId = action.payload;

    const { data } = yield call(
      request,
      `api/contest/contests/${contestId}`,
      {},
      {
        method: "GET",
      }
    );

    yield put(getContestDetailSuccess(data));
  } catch (error) {
    yield put(stopLoading());
    throw error;
  }
}

function* getActiveContests(action) {
  try {
    const params = action.payload || {};

    // Set default values if not provided
    if (!params.page) params.page = 1;
    if (!params.limit) params.limit = 100; // Get more contests for dropdown

    const { data } = yield call(
      request,
      "api/contest/active-contests",
      params,
      {
        method: "GET",
      }
    );

    yield put(getActiveContestsSuccess(data));
  } catch (error) {
    yield put(stopLoading());
  }
}

function* getVoteHistory(action) {
  try {
    const params = _pick(action.payload, ["page", "limit", "keyword"]);

    if (!params.page) params.page = 1;
    if (!params.limit) params.limit = 10;

    const { data } = yield call(
      request,
      "api/contest/admin/vote-history",
      params,
      {
        method: "GET",
      }
    );

    yield put(getVoteHistorySuccess(data));
  } catch (e) {
    yield put(stopLoading());
  }
}

function* updateVotingLogic(action) {
  try {
    const { contestId, votingLogic } = action.payload;
    const { meta } = action;

    const data = yield call(
      request,
      `api/contest/admin/contests/${contestId}/voting-logic`,
      { votingLogic },
      {
        method: "PUT",
      }
    );

    yield put(updateVotingLogicSuccess(data, meta));
  } catch (error) {
    yield put(stopLoading());
    throw error;
  }
}

function* onError() {
  yield put(stopLoading());
}

export default function* watchContestManager() {
  yield takeEvery(getContestListRequest.type, safe(getContestList, onError));
  yield takeEvery(createContestRequest.type, safe(createContest, onError));
  yield takeEvery(updateContestRequest.type, safe(updateContest, onError));
  yield takeEvery(deleteContestRequest.type, safe(deleteContest, onError));
  yield takeEvery(
    getContestDetailRequest.type,
    safe(getContestDetail, onError)
  );
  yield takeEvery(
    updateVotingLogicRequest.type,
    safe(updateVotingLogic, onError)
  );
  yield takeEvery(
    getActiveContestsRequest.type,
    safe(getActiveContests, onError)
  );
  yield takeEvery(getVoteHistoryRequest.type, safe(getVoteHistory, onError));
}
